import { Button } from "@nimit9/signet-ui/components/button"
import { Input } from "@nimit9/signet-ui/components/input"
import { RatioFill, RatioText } from "@nimit9/signet-ui/components/privacy-mask"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Trash2Icon } from "lucide-react"
import { useState } from "react"
import { useCurrencyFormat } from "@/hooks/use-currency-format"
import { api } from "@/lib/api"
import { cn } from "@/lib/cn"
import { formatDate } from "@/lib/format"
import { useUIStore } from "@/store/ui"

interface GoalDetailData {
  id: string
  name: string
  icon: string | null
  notes: string | null
  currentAmount: string | null
  targetAmount: string
  monthlyContribution: string | null
  monthsLeft: number | null
  eta: string | null
  percent: number
  status: string
  type: string
  owner: string | null
}

interface Contribution {
  id: string
  amount: string
  date: string
  notes: string | null
}

export function GoalDetail({ data }: { data: Record<string, unknown> }) {
  const fmt = useCurrencyFormat()
  const goal = data.goal as GoalDetailData
  const closePanel = useUIStore((s) => s.closePanel)
  const queryClient = useQueryClient()
  const [contributing, setContributing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [amount, setAmount] = useState("")
  const [notes, setNotes] = useState("")
  const [editing, setEditing] = useState(false)
  const [editTarget, setEditTarget] = useState(goal.targetAmount)
  const [editMonthly, setEditMonthly] = useState(goal.monthlyContribution ?? "")

  const editMutation = useMutation({
    mutationFn: () =>
      api.patch(`/goals/${goal.id}`, {
        targetAmount: editTarget,
        monthlyContribution: editMonthly || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] })
      setEditing(false)
      closePanel()
    },
  })

  const { data: contribs, isLoading } = useQuery({
    queryKey: ["goal-contributions", goal.id],
    queryFn: () =>
      api.get<{ success: boolean; data: Contribution[] }>(`/goals/${goal.id}/contributions`),
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/goals/${goal.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] })
      closePanel()
    },
  })

  const contributeMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      // The write route is /contribute; /contributions is read-only (UAT J7-02).
      api.post(`/goals/${goal.id}/contribute`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] })
      queryClient.invalidateQueries({ queryKey: ["goal-contributions", goal.id] })
      setContributing(false)
      setAmount("")
      setNotes("")
    },
  })

  // Removing a contribution also lowers the goal's saved amount (server side).
  const deleteContribution = useMutation({
    mutationFn: (id: string) => api.delete(`/goals/contributions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] })
      queryClient.invalidateQueries({ queryKey: ["goal-contributions", goal.id] })
    },
  })

  const current = parseFloat(goal.currentAmount || "0")
  const target = parseFloat(goal.targetAmount)
  const remaining = Math.max(0, target - current)

  const inputClass =
    "w-full rounded-lg border border-border-default bg-bg-elevated px-3 py-2 text-[13px] text-text-primary outline-none focus:border-accent-green"

  return (
    <div className="space-y-5">
      {/* Goal summary */}
      <div>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[16px] font-medium text-text-primary">
              {goal.icon && <span className="mr-1.5">{goal.icon}</span>}
              {goal.name}
            </p>
            {goal.notes && <p className="mt-0.5 text-[12px] text-text-muted">{goal.notes}</p>}
          </div>
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-[11px] font-medium",
              goal.percent >= 100
                ? "bg-accent-green/15 text-accent-green"
                : "bg-bg-elevated text-text-secondary",
            )}
          >
            <RatioText>{`${goal.percent}%`}</RatioText>
          </span>
        </div>

        <div className="mt-4 h-[6px] rounded-full bg-bg-elevated">
          <RatioFill
            pct={Math.min(goal.percent, 100)}
            className="h-full rounded-full bg-accent-green transition-all"
          />
        </div>

        <div className="mt-2 flex justify-between text-[12px]">
          <span className="text-text-primary font-medium">{fmt(current)}</span>
          <span className="text-text-muted">of {fmt(target)}</span>
        </div>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-bg-elevated p-3">
          <p className="text-[10px] uppercase tracking-wider text-text-muted">Remaining</p>
          <p className="mt-0.5 text-[15px] font-medium text-text-primary">{fmt(remaining)}</p>
        </div>
        <div className="rounded-lg bg-bg-elevated p-3">
          <p className="text-[10px] uppercase tracking-wider text-text-muted">Monthly</p>
          <p className="mt-0.5 text-[15px] font-medium text-text-primary">
            {goal.monthlyContribution ? `${fmt(goal.monthlyContribution)}` : "—"}
          </p>
        </div>
        {goal.monthsLeft !== null && (
          <div className="rounded-lg bg-bg-elevated p-3">
            <p className="text-[10px] uppercase tracking-wider text-text-muted">Months left</p>
            <p className="mt-0.5 text-[15px] font-medium text-text-primary">{goal.monthsLeft}</p>
          </div>
        )}
        {goal.eta && (
          <div className="rounded-lg bg-bg-elevated p-3">
            <p className="text-[10px] uppercase tracking-wider text-text-muted">ETA</p>
            <p className="mt-0.5 text-[15px] font-medium text-text-primary">
              {new Date(goal.eta).toLocaleDateString("en-IN", {
                month: "short",
                year: "numeric",
              })}
            </p>
          </div>
        )}
      </div>

      {/* Contribute */}
      {goal.status === "active" && goal.percent < 100 && (
        <div>
          {contributing ? (
            <div className="rounded-lg border border-accent-green/30 bg-bg-elevated p-3 space-y-2">
              <p className="text-[12px] font-medium text-text-primary">Log contribution</p>
              <input
                type="number"
                placeholder="Amount (₹)"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={inputClass}
              />
              <input
                type="text"
                placeholder="Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={inputClass}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => contributeMutation.mutate({ amount, notes })}
                  disabled={!amount || contributeMutation.isPending}
                  className="rounded-lg bg-accent-green/10 px-3 py-1.5 text-[12px] font-medium text-accent-green hover:bg-accent-green/20 disabled:opacity-50"
                >
                  {contributeMutation.isPending ? "Saving..." : "Add"}
                </button>
                <button
                  type="button"
                  onClick={() => setContributing(false)}
                  className="rounded-lg px-3 py-1.5 text-[12px] text-text-muted hover:text-text-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setContributing(true)}
              className="w-full rounded-lg border border-dashed border-border-default py-2 text-[12px] text-text-muted transition-colors hover:border-accent-green/40 hover:text-accent-green"
            >
              + Log contribution
            </button>
          )}
        </div>
      )}

      {/* Contribution history */}
      <div>
        <p className="mb-2 text-[11px] uppercase tracking-wider text-text-muted">History</p>
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-8 animate-pulse rounded-lg bg-bg-elevated" />
            ))}
          </div>
        ) : contribs?.data?.length ? (
          <div className="space-y-1">
            {contribs.data.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between rounded-lg px-2 py-1.5 hover:bg-bg-elevated"
              >
                <div>
                  <p className="text-[12px] text-text-primary">{formatDate(c.date)}</p>
                  {c.notes && <p className="text-[11px] text-text-muted">{c.notes}</p>}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-caption font-medium text-accent-green">
                    +{fmt(c.amount)}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon-touch"
                    className="md:size-8"
                    aria-label={`Delete contribution of ${fmt(c.amount)} on ${formatDate(c.date)}`}
                    disabled={deleteContribution.isPending}
                    onClick={() => deleteContribution.mutate(c.id)}
                  >
                    <Trash2Icon className="size-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[12px] text-text-muted">No contributions yet</p>
        )}
      </div>

      {/* Edit (UAT J7-02) */}
      {editing ? (
        <div className="space-y-2 rounded-lg border border-border-subtle p-3">
          <label className="block text-caption text-text-muted" htmlFor="goal-edit-target">
            Target amount
          </label>
          <Input
            id="goal-edit-target"
            type="number"
            inputMode="decimal"
            value={editTarget}
            onChange={(e) => setEditTarget(e.target.value)}
          />
          <label className="block text-caption text-text-muted" htmlFor="goal-edit-monthly">
            Monthly contribution
          </label>
          <Input
            id="goal-edit-monthly"
            type="number"
            inputMode="decimal"
            value={editMonthly}
            onChange={(e) => setEditMonthly(e.target.value)}
          />
          {editMutation.isError && (
            <p role="alert" className="text-caption text-accent-red">
              Couldn't save. Check the amounts and try again.
            </p>
          )}
          <div className="flex gap-2">
            <Button
              size="touch"
              onClick={() => editMutation.mutate()}
              disabled={editMutation.isPending || !(Number(editTarget) > 0)}
            >
              {editMutation.isPending ? "Saving…" : "Save"}
            </Button>
            <Button size="touch" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button size="touch" variant="outline" className="w-full" onClick={() => setEditing(true)}>
          Edit goal
        </Button>
      )}

      {/* Delete */}
      {confirmDelete ? (
        <div className="rounded-lg border border-accent-red/30 bg-accent-red/5 p-3">
          <p className="mb-2 text-[12px] text-text-primary">Delete this goal?</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              className="rounded-lg bg-accent-red/10 px-3 py-1.5 text-[12px] font-medium text-accent-red hover:bg-accent-red/20 disabled:opacity-50"
            >
              {deleteMutation.isPending ? "Deleting..." : "Yes, delete"}
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="rounded-lg px-3 py-1.5 text-[12px] text-text-muted hover:text-text-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirmDelete(true)}
          className="w-full rounded-lg border border-border-default py-2 text-[12px] text-text-muted hover:border-accent-red/30 hover:text-accent-red"
        >
          Delete goal
        </button>
      )}
    </div>
  )
}
